import type { Command } from '../../types/commands'
import { getDirectoryEntries, getRootEntries, isDirectory } from '../filesystem'
import { resolvePath, normalizeDirectoryPath, toDisplayPath } from '../pathUtils'
import { getSessionEntries } from '../sessionFilesystem'
import { getFileTypeColor, colorize, formatHelpText } from '../colorScheme'
import { parseCommandArgs, hasFlag } from '../argParser'
import { getContextDefaults } from '../../utils/contextUtils'

export const lsCommand: Command = {
  name: 'ls',
  description: 'List directory contents',
  usage: 'ls [-a] [path]',
  category: 'navigation',
  acceptsFiles: true,
  acceptsDirectories: true,
  execute: (args, context) => {
    // Parse command arguments
    const parsed = parseCommandArgs(args)
    const showHidden = hasFlag(parsed, 'a', 'all')
    const pathArg = parsed.positional[0]

    // Get context with defaults
    const { currentDirectory, sessionFs } = getContextDefaults(context)
    const targetPath = pathArg || currentDirectory

    // Resolve the path (handles relative, absolute, etc.)
    const resolvedPath = resolvePath(targetPath, currentDirectory)
    const normalizedPath = normalizeDirectoryPath(resolvedPath)

    // Check if directory exists
    if (!isDirectory(resolvedPath)) {
      return [
        {
          type: 'error',
          content: `ls: cannot access '${targetPath}': No such file or directory`,
        },
      ]
    }

    // Get directory entries from static filesystem
    const staticEntries = normalizedPath === '~'
      ? getRootEntries(showHidden)
      : getDirectoryEntries(normalizedPath, showHidden)

    // Get directory entries from session filesystem
    const sessionEntries = getSessionEntries(sessionFs, resolvedPath)

    // Merge entries (session entries come after static entries)
    const allEntries = [
      ...staticEntries,
      ...sessionEntries.map(node => node.name)
    ]

    // Remove duplicates (session overwrites static)
    const uniqueEntries = Array.from(new Set(allEntries))

    // Format output
    const displayPath = toDisplayPath(normalizedPath)

    // Format entries with / suffix for directories and markers for session files
    const formattedEntries = uniqueEntries.map(entry => {
      const entryPath = normalizedPath === '~' ? entry : `${normalizedPath}/${entry}`
      const sessionNode = sessionEntries.find(n => n.name === entry)

      // Check if it's a directory (from static filesystem OR session filesystem)
      const isDir = sessionNode
        ? sessionNode.type === 'directory'
        : isDirectory(entryPath)

      // Get appropriate color based on file type
      const color = getFileTypeColor(entry, isDir)

      // Build formatted entry with color
      let formatted = isDir ? `${entry}/` : entry
      const coloredEntry = colorize(formatted, color)

      // Mark session files with asterisk
      if (sessionNode) {
        const size = sessionNode.type === 'file' && sessionNode.content
          ? ` ${colorize(`(${sessionNode.content.length} bytes)`, 'brightBlack')}`
          : ''
        return `${coloredEntry}${size} ${colorize('*', 'yellow')}`
      }

      return coloredEntry
    })

    // Build output content with HTML
    let content = `\n📁 ${displayPath}\n\n`

    if (formattedEntries.length === 0) {
      content += '  (empty directory)\n'
    } else {
      content += formattedEntries.map(entry => `  ${entry}`).join('\n')
      content += '\n'
    }

    // Add helpful hints for root directory
    if (normalizedPath === '~') {
      const flagHint = showHidden
        ? formatHelpText('Hidden files shown (use -a flag)')
        : formatHelpText("Use 'ls -a' to show hidden files")

      content += `\n${flagHint}\n`
      content += `\n${formatHelpText("Type 'cat [filename]' to read files")}\n`
      content += `${formatHelpText("Type 'cd [directory]' to enter directories")}\n`
    }

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ),
      },
    ]
  },
}
